"use client"

import { useState, useEffect } from "react"
import { Plus, Edit2, Trash2, Check, X, BookOpen, Sparkles } from "lucide-react"
import { createCaste, getCastes, updateCaste, deleteCaste as apiDeleteCaste } from "../utils/api"

export default function Caste() {
  const [castes, setCastes] = useState([])
  const [newCaste, setNewCaste] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  // Track per-item actions
  const [savingId, setSavingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  // Fetch castes on component mount
  useEffect(() => {
    const fetchCastes = async () => {
      try {
        setIsLoading(true)
        const response = await getCastes()
        // Map backend _id to id to avoid undefined
        const mapped = response.data.map((c) => ({ id: c._id || c.id, name: c.name }))
        setCastes(mapped)
      } catch (error) {
        console.error("Error fetching castes:", error)
        setError("Failed to load castes.")
      } finally {
        setIsLoading(false)
      }
    }
    fetchCastes()
  }, [])

  const addCaste = async () => {
    if (!newCaste.trim()) return
    try {
      setSavingId("new") // Use a temporary ID for the new item being added
      const response = await createCaste({ name: newCaste.trim() })
      const newItem = { id: response.data._id, name: response.data.name }
      setCastes((prev) => [...prev, newItem])
      setNewCaste("")
    } catch (error) {
      console.error("Error adding caste:", error)
      setError("Failed to add caste.")
    } finally {
      setSavingId(null)
    }
  }

  const deleteCaste = async (id) => {
    try {
      setDeletingId(id)
      await apiDeleteCaste(id)
      setCastes((prev) => prev.filter((caste) => caste.id !== id))
    } catch (error) {
      console.error("Error deleting caste:", error)
      setError("Failed to delete caste.")
    } finally {
      setDeletingId(null)
    }
  }

  const startEditing = (caste) => {
    setEditingId(caste.id)
    setEditingName(caste.name)
  }

  const saveEdit = async () => {
    if (!editingName.trim()) return
    try {
      setSavingId(editingId)
      const response = await updateCaste(editingId, { name: editingName.trim() })
      setCastes((prev) => prev.map((c) => (c.id === editingId ? { ...c, name: response.data.name } : c)))
      cancelEdit()
    } catch (error) {
      console.error("Error updating caste:", error)
      setError("Failed to update caste.")
    } finally {
      setSavingId(null)
    }
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingName("")
  }

  const handleKeyPress = (e, action) => e.key === "Enter" && action()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-lg font-semibold text-gray-700">Loading castes...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 py-8 px-4">
      <div className="w-full mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Caste Management
            </h1>
          </div>
          <p className="text-gray-600 text-lg">Manage various castes for matrimony profiles</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl shadow-md flex items-center justify-between">
            <div className="flex items-center gap-2">
              <X className="w-5 h-5" />
              <strong className="font-bold">Error: </strong>
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-sm font-medium underline hover:text-red-900">
              Dismiss
            </button>
          </div>
        )}

        {/* Add Caste Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white">
            <div className="flex items-center gap-2">
              <Plus className="h-6 w-6" />
              <h2 className="text-xl font-semibold">Add New Caste</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Enter caste name..."
                  value={newCaste}
                  onChange={(e) => setNewCaste(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, addCaste)}
                  className="w-full h-12 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                  disabled={savingId === "new"}
                />
              </div>
              <button
                onClick={addCaste}
                disabled={!newCaste.trim() || savingId === "new"}
                className="h-12 px-8 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-xl font-semibold disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {savingId === "new" ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Add Caste
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Castes List */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-600 to-red-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-6 w-6" />
                <h2 className="text-xl font-semibold">Castes List</h2>
              </div>
              <div className="bg-white/20 px-4 py-2 rounded-full">
                <span className="font-semibold">
                  {castes.length} {castes.length === 1 ? "caste" : "castes"}
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {castes.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-12 h-12 text-orange-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Castes Added Yet</h3>
                <p className="text-gray-600">Start by adding your first caste above!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {castes.map((caste, index) => (
                  <div
                    key={caste.id}
                    className="group bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl p-4 transition-all duration-200 hover:shadow-md"
                  >
                    {editingId === caste.id ? (
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-lg">
                          <span className="text-orange-600 font-bold text-sm">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                        </div>
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, saveEdit)}
                          autoFocus
                          className="flex-1 h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          disabled={savingId === caste.id}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={saveEdit}
                            disabled={!editingName.trim() || savingId === caste.id}
                            className="h-10 px-4 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-lg font-medium disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            {savingId === caste.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Saving...
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4" />
                                Save
                              </>
                            )}
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={savingId === caste.id}
                            className="h-10 px-4 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-lg">
                            <span className="text-orange-600 font-bold text-sm">
                              {String(index + 1).padStart(2, "0")}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                              <BookOpen className="w-3 h-3 text-white" />
                            </div>
                            <span className="font-semibold text-gray-900 text-lg">{caste.name}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={() => startEditing(caste)}
                            disabled={deletingId === caste.id}
                            className="h-10 px-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => deleteCaste(caste.id)}
                            disabled={deletingId === caste.id}
                            className="h-10 px-4 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            {deletingId === caste.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Deleting...
                              </>
                            ) : (
                              <>
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
